/* Payroll building blocks shared by /payroll and /payroll/run. */
import { useState } from "react";
import { useReportCsv, type ReportKind } from "../../../data/hooks";
import type { Employee, MudadIssue, PayrollRun, PayrollRunView } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { downloadText, moneyText } from "../../../lib/format";
import { Alert, Button, errMsg, useToast } from "../../../ui";
import { unknownEmp } from "./common";

/** Run status label (prototype runStatus()). */
export function runStatus(r: PayrollRun): string {
  if (r.paid) return "Paid";
  if (r.approved) return "Approved";
  if (r.step >= 2) return "Pending approval";
  return "Draft";
}

/** Downloads a server CSV report (GET /reports/{kind}.csv) and saves it. */
export function ReportButton({ kind, file, label, variant, icon = "download", testId }: { kind: ReportKind; file: string; label: string; variant?: string; icon?: string; testId?: string }) {
  const q = useReportCsv(kind);
  const toast = useToast();
  const { L } = useI18n();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await q.refetch();
      if (r.error) throw r.error;
      if (r.data == null) throw new Error("empty report");
      downloadText(file, r.data);
      toast(L("Downloaded " + file, "تم تنزيل " + file));
    } catch (e) {
      toast(errMsg(e), "bad");
    } finally {
      setBusy(false);
    }
  };
  return <Button variant={variant} icon={icon} onClick={run} loading={busy} data-testid={testId ?? "btn-csv-" + kind}>{label}</Button>;
}

export function MudadAlert({ issues, emp, action, testId = "mudad-alert" }: { issues: MudadIssue[]; emp: Map<number, Employee>; action?: React.ReactNode; testId?: string }) {
  const { L, name, lang } = useI18n();
  if (!issues.length) return null;
  return (
    <Alert tone="warn" testId={testId}
      title={L(issues.length + " wage mismatches with GOSI / Qiwa", issues.length + " اختلافات في الأجور مع التأمينات / قوى")}
      desc={<>
        {issues.map((m, i) => (
          <span key={m.employeeId + m.kind} data-testid={"mudad-" + m.employeeId + "-" + m.kind}>
            {i > 0 && " · "}{name(emp.get(m.employeeId) ?? unknownEmp(m.employeeId))} ({m.kind} {(m.deviation * 100).toFixed(0)}% · {moneyText(m.registered, lang, 0)} → {moneyText(m.payroll, lang, 0)})
          </span>
        ))}
        {L(". Mudad rejects files where payroll differs from registered wages by more than 20%.", ". يرفض مدد الملفات عند اختلاف الأجر بأكثر من 20%.")}
      </>}
      action={action} />
  );
}

export function Art92Alert({ view, emp, okToo, testId = "art92-alert" }: { view: PayrollRunView; emp: Map<number, Employee>; okToo?: boolean; testId?: string }) {
  const { L, name, lang } = useI18n();
  if (!view.art92.length) {
    return okToo ? <Alert tone="ok" tint testId="art92-ok" title={L("All deductions are within the 50% limit (Art. 92)", "كل الاستقطاعات ضمن حد 50% (م 92)")} /> : null;
  }
  const over = view.slips.filter((s) => view.art92.includes(s.employeeId));
  return (
    <Alert tone="bad" testId={testId}
      title={L(over.length + " employees exceed the 50% deduction limit (Art. 92)", over.length + " موظفين يتجاوزون حد الاستقطاع 50% (م 92)")}
      desc={<>
        {over.map((s, i) => (
          <span key={s.employeeId} data-testid={"art92-" + s.employeeId}>
            {i > 0 && " · "}{name(emp.get(s.employeeId) ?? unknownEmp(s.employeeId))} {moneyText(s.cap.amount, lang)} / {moneyText(s.cap.limit, lang)}
          </span>
        ))}
        <div className="xs" style={{ marginTop: 4 }}>{L("Absence, other deductions and loan instalments may not exceed half of the wage. Approval is blocked until fixed.", "لا يجوز أن تتجاوز استقطاعات الغياب والخصومات وأقساط القروض نصف الأجر. الاعتماد موقوف حتى التصحيح.")}</div>
      </>} />
  );
}
